import { useState } from 'react'
import type { ReactNode } from 'react'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ConfirmDialog } from '@/components/Dialog'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { BareInput, BareSelect, Checkbox } from '@/components/Form'
import { SkeletonRows } from '@/components/Skeleton'
import {
  useAdminProfiles,
  useAdminUsers,
  useApplyProfile,
  useApproveUser,
  useDeleteUser,
  useRejectUser,
  useUpdateUser,
} from '@/hooks/useAdmin'
import { useNow } from '@/hooks/useNow'
import { formatDate, relativeTime, toDateInputValue, fromDateInputValue } from '@/lib/format'
import type { AdminUser, AdminUserPatch, Profile } from '@/lib/types'

interface Draft {
  label: string
  notes: string
  expiresAt: string
  disabled: boolean
  profileId: string
}

const toDraft = (user: AdminUser): Draft => ({
  label: user.label ?? '',
  notes: user.notes ?? '',
  expiresAt: toDateInputValue(user.expiresAt),
  disabled: user.disabled,
  profileId: user.profileId ?? '',
})

const profileNameFor = (user: AdminUser, profiles: Profile[]): string =>
  user.profileName ?? profiles.find((profile) => profile.id === user.profileId)?.name ?? '—'

/** Label above value, the way a board prints a column head above its flap. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="signage text-[9px] text-faint">{label}</dt>
      <dd className="mt-0.5 truncate font-mono text-xs text-ink">{value}</dd>
    </div>
  )
}

function UserBadges({ user }: { user: AdminUser }) {
  return (
    <>
      {user.isAdmin ? <Badge tone="accent">Admin</Badge> : null}
      {user.disabled ? <Badge tone="danger">Disabled</Badge> : null}
    </>
  )
}

/** The edit form, shared by the phone card and the table row. */
function UserEditor({
  user,
  profiles,
  onClose,
}: {
  user: AdminUser
  profiles: Profile[]
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(user))
  const update = useUpdateUser()

  function save() {
    const patch: AdminUserPatch = {
      label: draft.label.trim() || null,
      notes: draft.notes.trim() || null,
      expiresAt: fromDateInputValue(draft.expiresAt),
      disabled: draft.disabled,
      profileId: draft.profileId || null,
    }
    update.mutate({ id: user.id, patch }, { onSuccess: onClose })
  }

  return (
    <div className="grid gap-3 rounded-[3px] border border-line bg-canvas p-3 sm:grid-cols-2">
      <BareInput
        label="Label"
        placeholder="Label (shown to admins)"
        value={draft.label}
        onChange={(event) => setDraft({ ...draft, label: event.target.value })}
      />
      <BareSelect
        label="Profile"
        value={draft.profileId}
        onChange={(event) => setDraft({ ...draft, profileId: event.target.value })}
      >
        <option value="">No profile</option>
        {profiles.map((profile) => (
          <option key={profile.id} value={profile.id}>
            {profile.name}
          </option>
        ))}
      </BareSelect>
      <BareInput
        label="Expires at"
        type="date"
        value={draft.expiresAt}
        onChange={(event) => setDraft({ ...draft, expiresAt: event.target.value })}
      />
      <BareInput
        label="Notes"
        placeholder="Notes"
        value={draft.notes}
        onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
      />
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <Checkbox
          label="Disabled"
          checked={draft.disabled}
          onChange={(next) => setDraft({ ...draft, disabled: next })}
        />
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={update.isPending} onClick={save}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

function UserActions({
  user,
  editing,
  onEdit,
  onDelete,
  className = '',
}: {
  user: AdminUser
  editing: boolean
  onEdit: () => void
  onDelete: (user: AdminUser) => void
  className?: string
}) {
  const applyProfile = useApplyProfile()

  return (
    <div className={className}>
      <Button size="sm" onClick={onEdit} aria-expanded={editing}>
        {editing ? 'Close' : 'Edit'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        loading={applyProfile.isPending}
        onClick={() => applyProfile.mutate(user.id)}
        title="Re-apply this user's profile policy to Jellyfin"
      >
        Apply profile
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onDelete(user)}>
        Delete
      </Button>
    </div>
  )
}

/**
 * Phones get a stacked record, not a table sliced by a horizontal scrollbar:
 * the username reads as the row title and every other column becomes a
 * label/value pair underneath it.
 */
function UserRecord({
  user,
  profiles,
  editing,
  onEdit,
  onClose,
  onDelete,
  now,
}: {
  user: AdminUser
  profiles: Profile[]
  editing: boolean
  onEdit: () => void
  onClose: () => void
  onDelete: (user: AdminUser) => void
  now: number
}) {
  return (
    <div className="board-row px-4 py-3.5">
      <div className="flex items-start justify-between gap-2">
        <p className="signage-tight min-w-0 truncate text-sm text-ink">{user.username}</p>
        <div className="flex shrink-0 items-center gap-1.5">
          <UserBadges user={user} />
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
        <Field label="Profile" value={profileNameFor(user, profiles)} />
        <Field label="Expires" value={user.expiresAt ? formatDate(user.expiresAt) : 'Never'} />
        <Field label="Email" value={user.email ?? '—'} />
        <Field
          label="Last seen"
          value={user.lastLoginAt ? relativeTime(user.lastLoginAt, now) : '—'}
        />
        <div className="col-span-2 min-w-0">
          <dt className="signage text-[9px] text-faint">Notifications · App</dt>
          <dd className="mt-0.5">
            <SetupState user={user} />
          </dd>
        </div>
      </dl>

      <UserActions
        user={user}
        editing={editing}
        onEdit={onEdit}
        onDelete={onDelete}
        className="mt-3 grid grid-cols-2 gap-2 [&>button:last-child]:col-span-2"
      />

      {editing ? (
        <div className="mt-3">
          <UserEditor user={user} profiles={profiles} onClose={onClose} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * How far a user got through setup.
 *
 * Push subscriptions are counted server-side, so "On" means pushes can
 * genuinely be delivered — not that someone once tapped allow. Install state is
 * self-reported by the client, because an installed PWA and a browser tab are
 * indistinguishable over HTTP.
 */
/**
 * The approval queue, above the user list.
 *
 * Referred signups need a decision, and a decision buried in a table of forty
 * users gets missed — so anyone waiting is pulled to the top with the two
 * actions right there. It disappears entirely when the queue is empty.
 */
function ApprovalQueue({ users }: { users: AdminUser[] }) {
  const approve = useApproveUser()
  const reject = useRejectUser()
  const pending = users.filter((user) => user.approvalState === 'pending')

  if (pending.length === 0) return null

  return (
    <Card className="mb-5 border-warn/40">
      <CardHeader
        title={`Waiting for approval (${pending.length})`}
        description="Signed up through a referral link. They can see the portal but cannot watch anything until you let them in."
      />
      <CardBody flush>
        {pending.map((user) => (
          <div key={user.id} className="board-row px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="signage-tight text-sm text-ink">{user.username}</span>
              <span className="font-mono text-[11px] text-faint">
                {user.email ?? 'no email'}
                {user.createdAt ? ` · asked ${formatDate(user.createdAt)}` : ''}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                variant="primary"
                size="sm"
                loading={approve.isPending}
                onClick={() => approve.mutate(user.id)}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={reject.isPending}
                onClick={() => reject.mutate(user.id)}
              >
                Turn away
              </Button>
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

function SetupState({ user }: { user: AdminUser }) {
  const devices = user.pushDevices ?? 0
  const notifications =
    devices > 0
      ? { label: `On · ${devices}`, tone: 'text-ok' }
      : user.pushPermission === 'denied'
        ? { label: 'Blocked', tone: 'text-danger' }
        : { label: 'Off', tone: 'text-faint' }

  const install = user.pwaInstalled
    ? { label: 'Installed', tone: 'text-ok' }
    : user.clientSeenAt
      ? { label: 'Browser', tone: 'text-muted' }
      : { label: '—', tone: 'text-faint' }

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-mono text-[11px]">
      <span className={notifications.tone}>{notifications.label}</span>
      <span aria-hidden className="text-line-strong">
        ·
      </span>
      <span className={install.tone}>{install.label}</span>
    </span>
  )
}

const TH = 'signage px-4 py-2.5 text-[9px] font-semibold text-faint'
const TD = 'px-4 py-3 align-middle'

export function AdminUsersPage() {
  const users = useAdminUsers()
  const profiles = useAdminProfiles()
  const deleteUser = useDeleteUser()
  const now = useNow(60_000)
  const [query, setQuery] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null)

  const items = users.data?.items ?? []
  const profileItems = profiles.data?.items ?? []
  const filtered = query.trim()
    ? items.filter((user) =>
        `${user.username} ${user.label ?? ''} ${user.email ?? ''}`
          .toLowerCase()
          .includes(query.trim().toLowerCase()),
      )
    : items

  const toggle = (id: string) => setEditingId((current) => (current === id ? null : id))

  return (
    <>
      <ApprovalQueue users={items} />

      <Card>
      <CardHeader
        title={`Users${items.length ? ` (${items.length})` : ''}`}
        description="Merged from Jellyfin and Hermes."
        action={
          <div className="w-44">
            <BareInput
              label="Search users"
              type="search"
              placeholder="Search…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        }
      />
      <CardBody flush>
        {users.isPending ? (
          <div className="p-4">
            <SkeletonRows rows={6} />
          </div>
        ) : users.isError ? (
          <div className="p-4">
            <ErrorNotice error={users.error} onRetry={() => void users.refetch()} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title={items.length === 0 ? 'No users yet' : 'No matches'}
              description={
                items.length === 0
                  ? 'Users appear here once someone signs in or redeems an invite.'
                  : 'Try a different search.'
              }
            />
          </div>
        ) : (
          <>
            <div className="sm:hidden">
              {filtered.map((user) => (
                <UserRecord
                  key={user.id}
                  user={user}
                  profiles={profileItems}
                  editing={editingId === user.id}
                  onEdit={() => toggle(user.id)}
                  onClose={() => setEditingId(null)}
                  onDelete={setPendingDelete}
                  now={now}
                />
              ))}
            </div>

            <div className="hidden sm:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className={TH}>
                      User
                    </th>
                    <th scope="col" className={TH}>
                      Profile
                    </th>
                    <th scope="col" className={TH}>
                      Expires
                    </th>
                    <th scope="col" className={TH}>
                      Notifications · App
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <tr key={user.id} className="border-b border-line last:border-b-0">
                      <td className={TD}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="signage-tight text-sm text-ink">{user.username}</span>
                          <UserBadges user={user} />
                        </div>
                        <p className="mt-0.5 truncate font-mono text-[11px] text-faint">
                          {user.label ? `${user.label} · ` : ''}
                          {user.email ?? 'no email'}
                          {user.lastLoginAt ? ` · seen ${relativeTime(user.lastLoginAt, now)}` : ''}
                        </p>

                        {editingId === user.id ? (
                          <div className="mt-3">
                            <UserEditor
                              user={user}
                              profiles={profileItems}
                              onClose={() => setEditingId(null)}
                            />
                          </div>
                        ) : null}
                      </td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {profileNameFor(user, profileItems)}
                      </td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {user.expiresAt ? formatDate(user.expiresAt) : 'Never'}
                      </td>
                      <td className={TD}>
                        <SetupState user={user} />
                      </td>
                      <td className={TD}>
                        <UserActions
                          user={user}
                          editing={editingId === user.id}
                          onEdit={() => toggle(user.id)}
                          onDelete={setPendingDelete}
                          className="flex justify-end gap-2"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardBody>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        loading={deleteUser.isPending}
        destructive
        confirmLabel="Delete user"
        title={`Delete ${pendingDelete?.username ?? 'user'}?`}
        description="This removes the account in Jellyfin and in Hermes, including their request history here. It cannot be undone."
        onConfirm={() => {
          if (!pendingDelete) return
          deleteUser.mutate(
            { id: pendingDelete.id, username: pendingDelete.username },
            { onSettled: () => setPendingDelete(null) },
          )
        }}
      />
    </Card>
    </>
  )
}
